// src/components/journal/MemoryCard.jsx
import { useState } from 'react'
import { getMemoryEntry } from '../../utils/presenca'
import { getPromptForDate } from '../../utils/desafios'
import { supabase } from '../../supabaseClient'

const BookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(244,242,238,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
)

function truncate(text, max = 90) {
  if (!text) return ''
  return text.length <= max ? text : text.slice(0, max).trimEnd() + '…'
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
}

function MemoriaStory({ memory, prompt, label, userId, visible, onClose }) {
  const [responded, setResponded] = useState(false)
  const [showRewrite, setShowRewrite] = useState(false)
  const [rewriteText, setRewriteText] = useState('')
  const [rewriteSaved, setRewriteSaved] = useState(false)

  async function handleRespond(resonates) {
    if (userId) {
      await supabase
        .from('daily_logs')
        .update({ memory_resonates: resonates })
        .eq('user_id', userId)
        .eq('date', memory.date)
    }
    if (!resonates) {
      setShowRewrite(true)
    } else {
      setResponded(true)
      setTimeout(onClose, 1000)
    }
  }

  async function handleSaveRewrite() {
    setRewriteSaved(true)
    setTimeout(onClose, 1000)
  }

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 20px',
        background: visible ? 'rgba(20,18,40,0.72)' : 'rgba(20,18,40,0)',
        transition: 'background .3s ease',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'linear-gradient(150deg, #7b5ea7 0%, #9b7bc4 100%)',
        borderRadius: 28,
        padding: '28px 24px 28px',
        position: 'relative', overflow: 'hidden',
        maxHeight: '85vh', overflowY: 'auto',
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
        opacity: visible ? 1 : 0,
        transition: 'transform .32s cubic-bezier(.22,.68,0,1.2), opacity .28s ease',
      }}>
        {/* Círculo decorativo */}
        <div style={{
          position: 'absolute', bottom: -40, right: -40,
          width: 140, height: 140, borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
        }} />

        {/* Fechar */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 14, right: 14,
          width: 30, height: 30, borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)', border: 'none',
          color: 'rgba(244,242,238,0.7)', fontSize: 18,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1,
        }}>
          ×
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <BookIcon />
          </div>
          <div>
            <div style={{
              fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 12,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'rgba(244,242,238,0.95)',
            }}>Neste dia</div>
            <div style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 11,
              color: 'rgba(244,242,238,0.5)', marginTop: 1,
            }}>{label}</div>
          </div>
        </div>

        <div style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 11,
          color: 'rgba(244,242,238,0.35)', marginBottom: 16,
        }}>
          {formatDate(memory.date)}
        </div>

        {/* Prompt */}
        {prompt && (
          <div style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 13,
            color: 'rgba(244,242,238,0.55)', lineHeight: 1.5, marginBottom: 14,
          }}>
            {prompt}
          </div>
        )}

        {/* Aspas + citação completa */}
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 46, fontWeight: 700, lineHeight: 0.55,
          color: 'rgba(255,255,255,0.18)', userSelect: 'none', marginBottom: 4,
        }}>"</div>
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 19, fontStyle: 'italic',
          color: 'rgba(244,242,238,0.92)', lineHeight: 1.6, marginBottom: 28,
        }}>
          {memory.note}"
        </div>

        {/* Pergunta / reescrita / confirmação */}
        {!responded && !showRewrite && !rewriteSaved && (
          <>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 13,
              color: 'rgba(244,242,238,0.6)', textAlign: 'center', marginBottom: 12,
            }}>
              Você continua pensando assim hoje?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => handleRespond(true)} style={{
                flex: 1, padding: '12px 0', borderRadius: 14,
                background: 'rgba(111,174,143,0.22)',
                border: '1.5px solid rgba(111,174,143,0.45)',
                color: '#a8e6c8', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600, fontSize: 15, cursor: 'pointer',
              }}>Sim ✓</button>
              <button onClick={() => handleRespond(false)} style={{
                flex: 1, padding: '12px 0', borderRadius: 14,
                background: 'rgba(242,177,152,0.15)',
                border: '1.5px solid rgba(242,177,152,0.35)',
                color: '#f2b198', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600, fontSize: 15, cursor: 'pointer',
              }}>Não</button>
            </div>
          </>
        )}

        {showRewrite && !rewriteSaved && (
          <>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 13,
              color: 'rgba(244,242,238,0.6)', marginBottom: 10,
            }}>
              Como você pensaria sobre isso hoje?
            </p>
            <textarea
              value={rewriteText}
              onChange={e => setRewriteText(e.target.value)}
              placeholder="Escreva aqui..."
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.12)',
                border: '1.5px solid rgba(255,255,255,0.2)',
                borderRadius: 12, padding: '12px 14px',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 17, fontStyle: 'italic',
                color: 'rgba(244,242,238,0.9)',
                resize: 'none', outline: 'none',
                marginBottom: 12,
              }}
            />
            <button
              onClick={handleSaveRewrite}
              disabled={!rewriteText.trim()}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 14,
                background: rewriteText.trim() ? 'rgba(111,174,143,0.25)' : 'rgba(255,255,255,0.08)',
                border: `1.5px solid ${rewriteText.trim() ? 'rgba(111,174,143,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: rewriteText.trim() ? '#a8e6c8' : 'rgba(244,242,238,0.3)',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
                fontSize: 15, cursor: rewriteText.trim() ? 'pointer' : 'default',
              }}
            >
              Salvar reflexão
            </button>
          </>
        )}

        {(responded || rewriteSaved) && (
          <p style={{
            textAlign: 'center', fontFamily: 'DM Sans, sans-serif',
            fontSize: 15, color: 'rgba(244,242,238,0.6)',
          }}>Obrigada por refletir 🌱</p>
        )}
      </div>
    </div>
  )
}

export default function MemoryCard({ logs, userId }) {
  const [showStory, setShowStory] = useState(false)
  const [visible, setVisible] = useState(false)

  function openStory() {
    setShowStory(true)
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
  }

  function closeStory() {
    setVisible(false)
    setTimeout(() => setShowStory(false), 320)
  }

  const memory = getMemoryEntry(logs)
  if (!memory) return null

  const label = memory.weeksAgo === 1 ? '1 semana atrás' : `${memory.weeksAgo} semanas atrás`
  const prompt = getPromptForDate(memory.date)

  return (
    <>
      <button
        onClick={openStory}
        style={{
          width: '100%', textAlign: 'left',
          background: 'linear-gradient(150deg, #7b5ea7 0%, #9b7bc4 100%)',
          borderRadius: 16,
          padding: '18px 18px 14px',
          marginBottom: 16,
          border: 'none', cursor: 'pointer',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Círculo decorativo */}
        <div style={{
          position: 'absolute', bottom: -28, right: -28,
          width: 100, height: 100, borderRadius: '50%',
          background: 'rgba(255,255,255,0.07)',
          pointerEvents: 'none',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <BookIcon />
          </div>
          <div>
            <div style={{
              fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 12,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'rgba(244,242,238,0.95)', lineHeight: 1.2,
            }}>
              Neste dia
            </div>
            <div style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 11,
              color: 'rgba(244,242,238,0.5)', marginTop: 1,
            }}>
              {label}
            </div>
          </div>
        </div>

        {/* Prompt */}
        {prompt && (
          <div style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 12,
            color: 'rgba(244,242,238,0.5)', lineHeight: 1.4,
            marginBottom: 10,
          }}>
            {prompt}
          </div>
        )}

        {/* Aspas */}
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 46, fontWeight: 700, lineHeight: 0.55,
          color: 'rgba(255,255,255,0.18)', userSelect: 'none',
          marginBottom: 4,
        }}>
          "
        </div>

        {/* Citação truncada */}
        <div style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 16, fontStyle: 'italic',
          color: 'rgba(244,242,238,0.9)', lineHeight: 1.55,
          marginBottom: 12,
        }}>
          {truncate(memory.note)}"
        </div>

        {/* Ver tudo */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 11,
          color: 'rgba(244,242,238,0.45)', textAlign: 'right',
        }}>
          ver tudo →
        </div>
      </button>

      {showStory && (
        <MemoriaStory
          memory={memory}
          prompt={prompt}
          label={label}
          userId={userId}
          visible={visible}
          onClose={closeStory}
        />
      )}
    </>
  )
}
